/* ===================================================================
   Onboarding local state -- durable, re-hydratable inputs for the unified
   checklist home (Phase 3, 2026-07-07). Extracted out of routes/Onboarding.tsx
   so BOTH the full-screen /onboarding home AND the embedded zero-data
   dashboard checklist read/write the SAME per-tenant store (no divergence).

   WHAT IS PERSISTED (per tenant, in localStorage):
     - `activeTask`  -- which setup task is open (so an OAuth round-trip or a
       reload resumes exactly where the owner left off).
     - durable inputs (category / brand-voice draft / the Phase-4 diagnostic
       answers) that the owner typed but has not yet committed to the server.
   Server-derived facts (Google connection, customer count, brand voice on the
   profile, requests sent) are NEVER stored here -- they are re-read from the
   API and drive DERIVED completion. This store only holds re-hydratable UI
   inputs, never a fabricated "done" flag.

   REACTIVATION MARKER (the one setup item with no server read): the backend
   exposes no tenant-wide "past customers enrolled" signal, so a per-tenant
   marker is written ONLY on a SERVER-CONFIRMED enrollment success (the
   reactivation-enroll POST returned enrolled >= 1) -- materially different
   from a "clicked Next" flag. See useSetupProgress for the derivation and the
   documented limitation.
   =================================================================== */

/* ---- Onboarding task ids (the checklist launches these) ---- */
export type OnboardingTaskId =
  | 'diagnostic'
  | 'industry'
  | 'connect'
  | 'customers'
  | 'reactivation'
  | 'voice'
  | 'widget'
  | 'firstask';

const ONBOARDING_KEY_PREFIX = 'rhody_onboarding_';
const REACTIVATION_KEY_PREFIX = 'rhody_reactivation_enrolled_';

export interface OnboardingSaved {
  /* The open task, or null for the checklist home. */
  activeTask: OnboardingTaskId | null;
  category: string;
  tone: string;
  signoff: string;
  alwaysMention: string;
  /* Phase-4 20-second diagnostic (Item 0). Optional operational answers that
     pre-route the import step + seed brand-voice defaults. These are the LOCAL
     source of truth (synchronous, drives pre-routing without a round-trip); a
     best-effort copy is also persisted server-side in the brand_voice blob. */
  scheduleTool: string;
  pastCustomerEstimate: string;
}

function onboardingKey(clientId: string | undefined): string | null {
  return clientId ? ONBOARDING_KEY_PREFIX + clientId : null;
}

export function loadOnboarding(
  clientId: string | undefined,
): Partial<OnboardingSaved> | null {
  const key = onboardingKey(clientId);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed ? parsed : null;
  } catch {
    return null;
  }
}

export function saveOnboarding(
  clientId: string | undefined,
  state: OnboardingSaved,
): void {
  const key = onboardingKey(clientId);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    /* storage unavailable -- non-fatal */
  }
}

export function clearOnboardingForTenant(clientId: string | undefined): void {
  const key = onboardingKey(clientId);
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* non-fatal */
  }
}

/* True if this tenant has an in-progress onboarding saved (used by the
   Integrations screen to resume the checklist task after the Google OAuth
   round-trip instead of stranding the owner on /integrations). */
export function onboardingInProgress(clientId: string | undefined): boolean {
  const key = onboardingKey(clientId);
  if (!key) return false;
  try {
    return !!localStorage.getItem(key);
  } catch {
    return false;
  }
}

/* Clear EVERY tenant's onboarding key + reactivation marker (logout on a
   shared machine). Exported so logout can wipe them alongside the query cache
   + rr_ token. */
export function clearOnboardingState(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (
        k &&
        (k.startsWith(ONBOARDING_KEY_PREFIX) ||
          k.startsWith(REACTIVATION_KEY_PREFIX) ||
          // Linear-chute markers (lib/chuteState): test-sent + connect-skipped.
          k.startsWith('rhody_chute_'))
      ) {
        keys.push(k);
      }
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* storage unavailable -- non-fatal */
  }
}

/* -------------------------------------------------------------------
   Reactivation marker (soft Item 4 -- the one item without a server read).
   ------------------------------------------------------------------- */
function reactivationKey(clientId: string | undefined): string | null {
  return clientId ? REACTIVATION_KEY_PREFIX + clientId : null;
}

export function getReactivationEnrolled(clientId: string | undefined): boolean {
  const key = reactivationKey(clientId);
  if (!key) return false;
  try {
    return localStorage.getItem(key) != null;
  } catch {
    return false;
  }
}

/* Fired ONLY on a server-confirmed enrollment (enrolled >= 1). Dispatches a
   window event so useSetupProgress (mounted elsewhere, e.g. the dashboard)
   re-reads the marker without a manual reload. */
export function setReactivationEnrolled(
  clientId: string | undefined,
  count: number,
): void {
  const key = reactivationKey(clientId);
  if (!key) return;
  try {
    localStorage.setItem(
      key,
      JSON.stringify({ at: new Date().toISOString(), count }),
    );
    window.dispatchEvent(new CustomEvent('rhody:reactivation-enrolled'));
  } catch {
    /* non-fatal */
  }
}
